import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocaleStore } from '@/lib/core/stores';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useClientTranslation } from '@/lib/core/i18n';
import { LinkRouteUrl, randomUuid } from '@/lib/core/utils';
import { getEventDetail } from '@/services/platform/data/events';
import { SpinLoading } from '@/components/shared/loadings/spin';
import {
  resolveBookingScope,
  resolvePaymentDecision,
  resolveRegistrationErrorReason,
} from '@/components/shared/events/rules';
import type {
  EventCheckoutRetryState,
  EventCheckoutState,
  PaidRegistrationState,
} from '@/components/shared/checkout';
import { Toast } from '@/components/ui/toast';
import {
  EatAndDrinkBookFlowConfirmationPropertiesModel,
  EatAndDrinkBookFlowMoreDetailsPropertiesModel,
  EventBookFlowConfirmationElementModelContentType,
  EventBookFlowConfirmationPropertiesModel,
  EventBookFlowMoreDetailsElementModelContentType,
  EventBookFlowPaymentElementContentModelContentType,
  EventBookFlowWhereHowManyWhenElementContentModelContentType,
  EventBookFlowWhereHowManyWhenPropertiesModel,
  EventRegistrationDto,
  SeatingReservationDto,
} from '@/services/platform/api/model';
import type { BookingState, BookFlowEventProps } from './types';
import { EventBook } from './book';
import { EventRegistration, type EventRegistrationError } from './registration';
import { EventRegistrationConfirmation } from './confirmation';
import { EatAndDrinkDetails } from '../eat-and-drink/details';
import { ConfirmationBook } from '../eat-and-drink/confirmation/book';
import { ConfirmationRequest } from '../eat-and-drink/confirmation/request';
import {
  applyEventBookingPatch,
  capGuestsToSeatsLeft,
  dateKeyToLocalDate,
  deriveBookingUpdatesFromEvent,
  isDateBookable,
  restoreOccurrence,
} from './derive';
import { findStepIndex } from '../shared/steps';

export type { BookingState };

// ── BookFlowEvent ────────────────────────────────────────────────────────────
//
// Step 1 (More Details) and step 2 (Confirmation) only apply to `bookingMode ===
// 'SevenRooms'` (reuses the eat-and-drink components as-is) and `'Salesforce'`
// (a lighter registration + its own confirmation — `EventRegistrationDto` has no
// `publicGuid`, so it can't reuse the SevenRooms confirmation screens). `Concierge`
// and `External` events are handled directly on the Event Detail Page (opening chat
// or external URL respectively) and never enter this flow.

const makeEmptyBooking = (): BookingState => ({
  event: null,
  occurrence: null,
  service: null,
  venue: null,
  guestCount: null,
  date: null,
  room: null,
  time: null,
  isRequestAnyway: false,
  notes: '',
  groupNotes: {},
  selectedTags: [],
  selectedGroupTags: [],
  idempotencyKey: crypto.randomUUID(),
  seatingAreaPublicGuid: undefined,
});

export function BookFlowEvent({ content, context }: BookFlowEventProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useLocaleStore(state => state.locale);
  const setHideNavbar = useUIStore(state => state.setHideNavbar);
  // Deliberately a plain number: the CMS authors four steps on this flow today, so `0 | 1 | 2` was an
  // assertion that had quietly become false.
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState<BookingState>(makeEmptyBooking);
  const [reservation, setReservation] = useState<SeatingReservationDto | null>(null);
  const [registration, setRegistration] = useState<EventRegistrationDto | null>(null);
  const [errorToastMessage, setErrorToastMessage] = useState<string | null>(null);
  const [errorToastKey, setErrorToastKey] = useState(0);
  // The Events Calendar day the member came in with, when it can no longer be booked. Held for as
  // long as they stay on that event — "When" stays a free choice for them — and cleared on another.
  const [unavailableDate, setUnavailableDate] = useState<Date | null>(null);
  // Returning from checkout, step 0 (the Which/Where/How many/When picker) would otherwise flash
  // for the length of the preselect fetch below before `setStep(2)` lands — true only for that
  // resume case; every other entry shows the book flow immediately as it always has. Cleared once
  // the fetch settles either way (see the effect below), never re-armed.
  const [isPreselecting, setIsPreselecting] = useState(() => {
    const state = location.state as Partial<PaidRegistrationState> | null;
    return !!state?.paidRegistration;
  });

  const steps = content.properties?.steps?.items ?? [];

  /**
   * Where the confirmation actually is, derived rather than assumed.
   *
   * Every path that finishes a booking used to say `setStep(2)`. Then the CMS authored an
   * `eventBookFlowPayment` step at index 2, the confirmation moved to 3, and all four of those paths
   * started landing on a step with no `case` — rendering nothing, so a customer who had just paid saw
   * a blank screen. The order of the steps belongs to the editor, so the index has to be looked up.
   *
   * Declared above the effects on purpose: `goToConfirmation` is one of their dependencies, and a
   * dependency array is evaluated during render, so declaring it further down would read it in its
   * temporal dead zone.
   */
  const confirmationStep = findStepIndex(
    steps,
    EventBookFlowConfirmationElementModelContentType.eventBookFlowConfirmation
  );

  /** The single way to finish a booking. One place to be wrong beats four. */
  const goToConfirmation = useCallback(() => {
    if (confirmationStep === -1) {
      console.warn(
        'BookFlowEvent: no eventBookFlowConfirmation step is authored on this flow, so there is ' +
          'nowhere to send the customer after booking. Add the step in the CMS.'
      );
      return;
    }
    setStep(confirmationStep);
  }, [confirmationStep]);

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);

  /** Re-keyed so the same message twice in a row still shows. Declared above the effects that use it. */
  const showErrorToast = useCallback((message: string) => {
    setErrorToastKey(key => key + 1);
    setErrorToastMessage(message);
  }, []);

  // Arriving from the Event Detail Page's "Book now" already knows which event — pre-select
  // it instead of making the user pick it again in the Which sheet. Mirrors how the eat-and-drink/
  // experience flows pre-select a venue from `location.state.venueNodeKey`.
  const hasPreselected = useRef(false);
  useEffect(() => {
    const state = location.state as
      | (Partial<PaidRegistrationState> &
          Partial<EventCheckoutRetryState> & { eventId?: string; selectedDate?: string })
      | null;
    const preselectId = state?.eventId;
    if (!preselectId || hasPreselected.current) return;
    hasPreselected.current = true;

    // Returning from checkout: the payment succeeded and the server already registered the event, so
    // this rehydrates the booking and jumps straight to the confirmation rather than replaying the
    // registration step (which would submit a second, unpaid registration).
    const paid = state?.paidRegistration;
    // Returning from checkout refused before charging — fewer seats than the party, or a date that
    // no longer exists. The fetch below is the re-read; the toast says what to change.
    const seatsLeft = state?.seatsLeft;
    if (seatsLeft != null) showErrorToast(t('App.Events.RegistrationErrorSeatsLeft', { count: seatsLeft }));
    else if (state?.dateGone) showErrorToast(t('App.Events.RegistrationErrorDateGone'));

    getEventDetail(preselectId, locale).then(detail => {
      if (!detail) {
        // The fetch failed while resuming a checkout — fall back to showing the book flow rather
        // than leaving the spinner up forever with nothing left to resolve it.
        setIsPreselecting(false);
        return;
      }
      const eventPatch = deriveBookingUpdatesFromEvent(detail);
      // The day tapped in the Events Calendar. Only honored once the event resolved to a single
      // venue — a multi-venue event has no venue yet, so `applyEventBookingPatch` can't derive an
      // occurrence from a date alone, and the date would just be discarded once "Where" is answered.
      const selectedDate =
        state?.selectedDate && eventPatch.venue ? dateKeyToLocalDate(state.selectedDate) : undefined;
      // A day that can no longer be booked is not applied and not swapped for another date: "When"
      // is left empty and the member is told, so they never book a date they did not choose.
      const selectedDateBookable = selectedDate ? isDateBookable(detail, selectedDate) : false;
      if (selectedDate && !selectedDateBookable) setUnavailableDate(selectedDate);

      setBooking(prev => {
        // A locked "When" keeps the occurrence's own date: a day tapped mid-way through a Salesforce
        // occurrence spanning several days books that occurrence, which is not dated on that day.
        const patch = !selectedDate
          ? eventPatch
          : selectedDateBookable
            ? { ...eventPatch, date: eventPatch.date ?? selectedDate }
            : { ...eventPatch, date: null, time: null };
        const next = applyEventBookingPatch(prev, patch);
        // `dateGone` needs nothing more: the re-read event no longer lists that date, so the fresh
        // derivation above has already dropped it.
        if (seatsLeft != null) {
          return capGuestsToSeatsLeft(restoreOccurrence(next, detail, state?.occurrenceId), seatsLeft);
        }
        if (!paid) return next;
        const occurrence =
          detail.occurrences?.find(item => item.id === state?.occurrenceId) ?? next.occurrence;
        return applyEventBookingPatch(next, {
          occurrence,
          guestCount: state?.numberOfGuests ?? next.guestCount,
        });
      });
      if (paid) {
        // `paid` only knows the payment succeeded — it says nothing about whether the server's
        // Salesforce registration write actually landed. `detail.myRegistration` is the verified
        // record; trust it when it is for the occurrence just paid for. Match on `occurrenceId`
        // rather than trusting `myRegistration` unconditionally: it reflects the caller's *most
        // recent active* registration on the event, which could be a stale one for a different
        // occurrence if the customer has booked this event before. Falling back to `paid` when
        // there's no match also covers the ordinary case — the registration write can be a beat
        // behind the payment settling.
        const serverRegistration =
          detail.myRegistration?.occurrenceId === state?.occurrenceId ? detail.myRegistration : undefined;
        setRegistration(serverRegistration ?? paid);
        goToConfirmation();
        setIsPreselecting(false);
      }
    });
  }, [location.state, locale, goToConfirmation, showErrorToast, t]);

  const bookingMode = booking.event?.bookingMode;

  const handleBack = () => {
    if (step === 0) navigate(-1);
    else setStep(prev => prev - 1);
  };

  // A `request`-type slot (or an unavailable one) is already reserved as Pending by `WhenSheet`
  // itself — jump straight to the confirmation, skipping More Details. Without this the flow
  // would fall through to `EatAndDrinkDetails`, whose `/bookings` call SevenRooms rejects on a
  // slot that only accepts requests. Mirrors the eat-and-drink/parking/experience flows.
  const handleRequestSuccess = (res: SeatingReservationDto) => {
    setReservation(res);
    goToConfirmation();
  };

  /**
   * Re-reads the event after the server said our copy is stale. `dropDate` (the date no longer
   * exists) re-derives the booking from scratch, keeping the party when the new limit still allows
   * it; otherwise only the event is swapped, so the chosen occurrence picks up its fresh counts and
   * `book.tsx`'s sold-out gate reflects them. Both get a new idempotency key — a retry is a new request.
   */
  const refreshEvent = (eventId: string, { dropDate }: { dropDate: boolean }) => {
    getEventDetail(eventId, locale).then(detail => {
      if (!detail) return;
      setBooking(prev => {
        if (!dropDate) return applyEventBookingPatch(prev, { event: detail, idempotencyKey: randomUuid() });
        const next = applyEventBookingPatch(prev, deriveBookingUpdatesFromEvent(detail));
        const max = next.venue?.guestsMax;
        const keepParty = prev.guestCount != null && (max == null || prev.guestCount <= max);
        return applyEventBookingPatch(next, {
          ...(keepParty ? { guestCount: prev.guestCount } : {}),
          idempotencyKey: randomUuid(),
        });
      });
    });
  };

  const handleRegistrationError = ({ status, detail, availableSpots }: EventRegistrationError) => {
    const reason = resolveRegistrationErrorReason(status, detail);
    setStep(0);

    // 409 is only ever "not enough seats". Seats still left → re-ask "How many" with that cap;
    // none left → sold out; unreadable (null) → sold out, and re-read so the counts catch up.
    if (reason === 'soldOut') {
      if (availableSpots != null && availableSpots > 0) {
        setBooking(prev => capGuestsToSeatsLeft(prev, availableSpots));
        showErrorToast(t('App.Events.RegistrationErrorSeatsLeft', { count: availableSpots }));
        return;
      }
      showErrorToast(t('App.Events.RegistrationErrorSoldOut'));
      if (availableSpots === null && booking.event?.id) refreshEvent(booking.event.id, { dropDate: false });
      return;
    }
    if (reason === 'dateGone') {
      showErrorToast(t('App.Events.RegistrationErrorDateGone'));
      if (booking.event?.id) refreshEvent(booking.event.id, { dropDate: true });
      return;
    }

    showErrorToast(
      reason === 'closed'
        ? t('App.Events.RegistrationErrorClosed')
        : reason === 'paymentRequired'
          ? t('App.Events.RegistrationErrorPaymentRequired')
          : reason === 'invalid'
            ? t('App.Events.RegistrationErrorInvalid')
            : t('App.Events.RegistrationErrorUnknown')
    );
  };

  const handleBookingError = () => {
    showErrorToast(t('App.Labels.BookingError'));
    setStep(0);
  };

  /**
   * A paid event is charged before it is registered: the backend's event payment handler creates the
   * Salesforce registration inside the payment's success path, and `registerEventV1` rejects a paid
   * event outright without a payment reference (contract §3.1.2). So a paid booking leaves this flow
   * for the checkout route and comes back with a registration already made.
   *
   * Only `Salesforce` is diverted. `SevenRooms` events reserve through a different path that carries
   * no price, and `Concierge`/`External` never enter this flow at all.
   */
  const routeToCheckoutIfPaid = (): boolean => {
    if (bookingMode !== 'Salesforce' || !booking.event?.id) return false;

    const scope = resolveBookingScope(booking.event, booking.occurrence);
    if (resolvePaymentDecision(scope) !== 'paid') return false;

    const checkoutPath = LinkRouteUrl(context?.settings?.properties?.checkout?.route);
    if (!checkoutPath) {
      // No authored checkout page means there is nowhere to take payment. Failing loudly here beats
      // falling through to a registration the API will reject with a payment-required error.
      console.warn('BookFlowEvent: paid event but no checkout route configured in app settings.');
      showErrorToast(t('App.Events.RegistrationErrorPaymentRequired'));
      return true;
    }

    const eventCheckout: EventCheckoutState = {
      eventId: booking.event.id,
      occurrenceId: booking.occurrence?.id,
      numberOfGuests: booking.guestCount ?? undefined,
      venueName: booking.occurrence?.venueName ?? booking.venue?.name ?? undefined,
      occurrenceStart: booking.occurrence?.startDateTime ?? undefined,
      unitPrice: scope.price ?? undefined,
      currencyCode: scope.currencyCode ?? undefined,
      returnPath: location.pathname,
    };
    navigate(checkoutPath, { state: { eventCheckout } });
    return true;
  };

  const handleContinue = () => {
    setErrorToastMessage(null);
    if (routeToCheckoutIfPaid()) return;

    switch (bookingMode) {
      case 'SevenRooms':
      case 'Salesforce':
        setStep(1);
        return;
      default:
        // 'None', 'Concierge', 'External' — shouldn't be reachable. Concierge/External
        // are handled directly on the Event Detail Page; None events don't trigger
        // this flow (allowsBooking: false prevents them from appearing in Which sheet).
        return;
    }
  };

  return (
    <div className="pt-safe-area relative flex h-full flex-col bg-white">
      {isPreselecting && <SpinLoading wrapperClassName="min-h-screen" />}
      {!isPreselecting &&
        steps.map((stepItem, index) => {
          if (index !== step) return null;
          const props = stepItem.content?.properties;
          const stepType = stepItem.content?.contentType;
          switch (stepType) {
            case EventBookFlowWhereHowManyWhenElementContentModelContentType.eventBookFlowWhereHowManyWhen:
              return (
                <EventBook
                  key={index}
                  cmsProps={props as EventBookFlowWhereHowManyWhenPropertiesModel}
                  booking={booking}
                  onBookingChange={updates => {
                    // Kept after the member picks a date, so "When" stays open to change it again;
                    // only another event leaves the day that came in behind.
                    if (updates.event) setUnavailableDate(null);
                    setBooking(prev => applyEventBookingPatch(prev, updates));
                  }}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  onRequestSuccess={handleRequestSuccess}
                  context={context}
                  step={index + 2}
                  unavailableDate={unavailableDate}
                />
              );

            case EventBookFlowMoreDetailsElementModelContentType.eventBookFlowMoreDetails:
              if (bookingMode === 'SevenRooms') {
                return (
                  <EatAndDrinkDetails
                    key={index}
                    cmsProps={props as EatAndDrinkBookFlowMoreDetailsPropertiesModel}
                    booking={booking}
                    onBookingChange={updates => setBooking(prev => applyEventBookingPatch(prev, updates))}
                    onBack={handleBack}
                    onSuccess={res => {
                      setReservation(res);
                      goToConfirmation();
                    }}
                    onError={handleBookingError}
                    step={index + 2}
                  />
                );
              }
              if (bookingMode === 'Salesforce') {
                return (
                  <EventRegistration
                    key={index}
                    booking={booking}
                    onSuccess={reg => {
                      setRegistration(reg);
                      goToConfirmation();
                    }}
                    onError={handleRegistrationError}
                  />
                );
              }
              return null;

            case EventBookFlowConfirmationElementModelContentType.eventBookFlowConfirmation:
              if (bookingMode === 'SevenRooms') {
                if (!reservation) return null;
                return booking.isRequestAnyway ? (
                  <ConfirmationRequest
                    key={index}
                    booking={booking}
                    reservation={reservation}
                    onGoHome={() => navigate('/')}
                    onCancelRequest={() => navigate(-1)}
                  />
                ) : (
                  <ConfirmationBook
                    key={index}
                    booking={booking}
                    reservation={reservation}
                    cmsProps={props as EatAndDrinkBookFlowConfirmationPropertiesModel}
                    onGoHome={() => navigate('/')}
                    onSeeBookingDetails={() =>
                      navigate(
                        `${context?.settings?.properties?.bookingDetail?.route?.path}/${reservation.publicGuid}`
                      )
                    }
                  />
                );
              }
              if (bookingMode === 'Salesforce') {
                if (!registration) return null;
                return (
                  <EventRegistrationConfirmation
                    key={index}
                    booking={booking}
                    registration={registration}
                    cmsProps={props as EventBookFlowConfirmationPropertiesModel}
                    onGoHome={() => navigate('/')}
                    onSeeBookingDetails={() =>
                      navigate(
                        `${context?.settings?.properties?.bookingDetail?.route?.path}/${registration.id}`
                      )
                    }
                  />
                );
              }
              return null;

            /*
              Authored but deliberately inert. Event payment happens on the `/checkout` route — see
              `routeToCheckoutIfPaid` — so this step is only ever passed through, never landed on.
              Explicit rather than left to `default`: it was falling through to that silent `null`
              that put paying customers on a blank screen, and a skipped step has to read as a
              decision. Turning it into a real inline payment step (as sunbed/stay/experience do via
              `PaymentStepContentTypes`) is the open follow-up.
            */
            case EventBookFlowPaymentElementContentModelContentType.eventBookFlowPayment:
              return null;

            default:
              // The next step the CMS gains should name itself here rather than cost another round of
              // diagnosis from a blank screen.
              console.warn(`BookFlowEvent: no renderer for authored step type "${stepType}".`);
              return null;
          }
        })}

      <Toast
        key={errorToastKey}
        variant="error"
        open={errorToastMessage !== null}
        message={errorToastMessage ?? ''}
        onDismiss={() => setErrorToastMessage(null)}
      />
    </div>
  );
}
